import { Pencil, Plus, X } from "lucide-react";
import { type FormEvent, useState } from "react";

import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useCurrentUser } from "@/hooks/use-current-user";
import { cn } from "@/lib/utils";

import { useUpdatePassenger } from "../../api/use-claim-mutations";
import { ACTION_CLASS, stopPropagation } from "./passenger-email-actions";
import type { PassengerRow } from "./passenger-grouping";

/** The birthdate is stored at midnight UTC: its `YYYY-MM-DD` is the date input's value. */
function toInputValue(birthDate: Date | string): string {
  const date = new Date(birthDate);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

function todayInputValue(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Edit (pencil) or remove (red ×) a passenger's birthdate, shown on hover next to it (TECH-1393).
 * Without one, « + Date de naissance » adds it back. Upclaim users only.
 *
 * The GDS sometimes carries a wrong birthdate: a passenger shown as a minor has their assignment of
 * claim signed by a legal representative. Without a birthdate the passenger counts as an adult.
 */
export function PassengerBirthdateActions({ claimId, row }: { claimId: string; row: PassengerRow }) {
  const [editOpen, setEditOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [birthdate, setBirthdate] = useState("");
  const updatePassenger = useUpdatePassenger(claimId);
  const { isUpclaim } = useCurrentUser();

  const userSegmentId = row.id;
  const current = row.passenger.birthDate ? toInputValue(row.passenger.birthDate) : "";
  if (userSegmentId === null || !isUpclaim) {
    return null;
  }

  function openEdit() {
    setBirthdate(current);
    setEditOpen(true);
  }

  function save(event: FormEvent) {
    event.preventDefault();
    if (userSegmentId === null || !birthdate) {
      return;
    }
    updatePassenger.mutate(
      { userSegmentId, birthdate },
      { onSuccess: ({ success }) => success && setEditOpen(false) },
    );
  }

  function remove() {
    if (userSegmentId === null) {
      return;
    }
    updatePassenger.mutate(
      { userSegmentId, birthdate: null },
      { onSuccess: ({ success }) => success && setRemoveOpen(false) },
    );
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: only stops the row's drag / selection handlers
    // biome-ignore lint/a11y/useKeyWithClickEvents: only stops the row's drag / selection handlers
    <span
      className="inline-flex shrink-0 items-center"
      onPointerDown={stopPropagation}
      onClick={stopPropagation}
    >
      {current ? (
        <>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Modifier la date de naissance de ${row.passenger.civility}`}
                  className={cn(ACTION_CLASS, "size-5 text-gray-400 hover:text-gray-700 sm:size-5")}
                  onClick={openEdit}
                />
              }
            >
              <Pencil className="size-3" aria-hidden="true" />
            </TooltipTrigger>
            <TooltipContent>Modifier la date de naissance</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Retirer la date de naissance de ${row.passenger.civility}`}
                  className={cn(
                    ACTION_CLASS,
                    "size-5 text-red-500 hover:bg-red-50 hover:text-red-600 sm:size-5",
                  )}
                  onClick={() => setRemoveOpen(true)}
                />
              }
            >
              <X className="size-3.5" aria-hidden="true" />
            </TooltipTrigger>
            <TooltipContent>Retirer la date de naissance</TooltipContent>
          </Tooltip>

          <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Retirer la date de naissance de {row.passenger.civility} ?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Sans date de naissance, ce passager est considéré comme majeur, y compris pour la signature
                  de la cession de créance.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogClose render={<Button variant="outline" />}>Annuler</AlertDialogClose>
                <Button variant="destructive" loading={updatePassenger.isPending} onClick={remove}>
                  Retirer la date
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      ) : (
        <Button
          variant="ghost"
          size="xs"
          aria-label={`Ajouter la date de naissance de ${row.passenger.civility}`}
          className={cn(ACTION_CLASS, "h-5 px-1 font-normal text-gray-400 hover:text-gray-700 sm:h-5")}
          onClick={openEdit}
        >
          <Plus className="size-3" aria-hidden="true" />
          Date de naissance
        </Button>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          {/* `contents`: header, panel and footer stay the popup's own flex children. */}
          <form className="contents" onSubmit={save}>
            <DialogHeader>
              <DialogTitle>Date de naissance de {row.passenger.civility}</DialogTitle>
            </DialogHeader>
            <DialogPanel>
              <Field>
                <FieldLabel>Date de naissance</FieldLabel>
                <Input
                  type="date"
                  autoFocus
                  max={todayInputValue()}
                  value={birthdate}
                  onChange={(event) => setBirthdate(event.target.value)}
                />
              </Field>
            </DialogPanel>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Annuler
              </Button>
              <Button
                type="submit"
                loading={updatePassenger.isPending}
                disabled={!birthdate || birthdate === current}
              >
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </span>
  );
}
